import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useRegistrationFeeShare } from "../hooks/useRegistrationFeeShare";
import { useRegistrationFeeConfig } from "../../../feeconfig/registrationFeeConfig/hooks/useRegistrationFeeConfig";
import { useCourse } from "../../../configuration/course/hooks/useCourse";
import { useSession } from "../../../configuration/session/hooks/useSession";
import { useFeeType } from "../../../feetypes/hooks/useFeeType";
import { useCenter } from "../../../peoples/centers/hooks/useCenter";
import { formatDateToYMD } from "../../../../core/utils/dateFormatter";

const ViewRegistrationFeeShare = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Use the same hooks as edit mode
  const { getSessionState } = useSession();
  const { getCourseState } = useCourse();
  const { getFeeTypeState } = useFeeType();
  const { useCenterList } = useCenter({ skip: true });
  const { useGetFeeShareById } = useRegistrationFeeShare();
  const { useGetFeeConfigById: useGetRegistrationFeeById } =
    useRegistrationFeeConfig();

  // Get fee share data
  const { feeShare, loading: isLoadingFeeShare } = useGetFeeShareById(id || "");

  // Get session, course, fee type and center data exactly like in edit mode
  const { sessions, isLoading: isLoadingSessions } = getSessionState();
  const { courses, isLoading: isLoadingCourses } = getCourseState();
  const { feetypes, isLoading: isLoadingFeeTypes } = getFeeTypeState();
  const { data: centerResponse, isLoading: isLoadingCenters } = useCenterList();
  const centers = centerResponse?.data?.items || [];

  // State to store fee config data
  const [registrationFeeConfigId, setRegistrationFeeConfigId] =
    useState<string>("");
  const {
    feeConfig: registrationFeeConfig,
    loading: isLoadingRegistrationFeeConfig,
  } = useGetRegistrationFeeById(registrationFeeConfigId);

  // State to store registration fee amount
  const [registrationFeeAmount, setRegistrationFeeAmount] = useState<number>(0);

  // Get fee type for Registration Fee
  const registrationFeeTypeId = useMemo(() => {
    const registrationFeeType = feetypes.find(
      (feetype) => feetype.type === "registrationfee"
    );
    return registrationFeeType?.id || 0;
  }, [feetypes]);

  // Find session name dynamically from sessions array
  const sessionName = useMemo(() => {
    if (feeShare && feeShare.session_id) {
      const session = sessions.find((s) => s.id === feeShare.session_id);
      return session?.name || "N/A";
    }
    return "N/A";
  }, [feeShare, sessions]);

  // Find course name dynamically from courses array
  const courseName = useMemo(() => {
    if (feeShare && feeShare.course_id) {
      const course = courses.find((c) => c.id === feeShare.course_id);
      return course?.name || "N/A";
    }
    return "N/A";
  }, [feeShare, courses]);

  // Set the fee config ID when fee share data is loaded
  useEffect(() => {
    if (feeShare && feeShare.course_id) {
      setRegistrationFeeConfigId(feeShare.course_id.toString());
    }
  }, [feeShare]);

  // Update registration fee amount when fee config is loaded - using same approach as edit mode
  useEffect(() => {
    if (registrationFeeConfig?.registrationfee) {
      const feeAmount =
        parseFloat(registrationFeeConfig.registrationfee.fee) || 0;
      setRegistrationFeeAmount(feeAmount);
    } else {
      setRegistrationFeeAmount(0);
    }
  }, [registrationFeeConfig]);

  // Loading state for all data
  const isLoading = isLoadingFeeShare || isLoadingRegistrationFeeConfig;

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading registration fee share data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!feeShare) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="alert alert-warning">
                <h5>Fee share not found</h5>
                <p>
                  The requested registration fee share record could not be
                  found.
                </p>
                <Link
                  to={all_routes.registrationFeeShareList}
                  className="btn btn-outline-primary mt-2"
                >
                  Return to Registration Fee Share List
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate share values based on share type
  const calculatePercentage = () => {
    if (registrationFeeAmount <= 0 || !feeShare.share_amount) return 0;
    return (feeShare.share_amount / registrationFeeAmount) * 100;
  };

  const calculateFixedAmount = () => {
    if (!feeShare.share_amount || registrationFeeAmount <= 0) return 0;
    return (feeShare.share_amount / 100) * registrationFeeAmount;
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="mb-1">Registration Fee Share Details</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.registrationFeeShareList}>
                    Registration Fee Shares
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  View Details
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2">
            <button
              onClick={() => navigate(-1)}
              className="btn btn-outline-primary"
            >
              <i className="ti ti-arrow-left me-1"></i> Back
            </button>
            <Link
              to={`${all_routes.editRegistrationFeeShare.replace(
                ":id",
                id || ""
              )}`}
              className="btn btn-primary"
            >
              <i className="ti ti-edit me-1"></i> Edit
            </Link>
          </div>
        </div>

        {/* Basic Info Card */}
        <div className="card mb-3">
          <div className="card-header bg-light">
            <div className="d-flex align-items-center">
              <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                <i className="ti ti-info-square-rounded fs-16" />
              </span>
              <h4 className="text-dark mb-0">Basic Information</h4>
            </div>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-4">
                <div className="d-flex">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-sm bg-light-primary rounded">
                      <i className="ti ti-calendar fs-16 text-primary"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="fw-bold mb-1">Session</h6>
                    <p className="fw-medium mb-0">{sessionName}</p>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="d-flex">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-sm bg-light-info rounded">
                      <i className="ti ti-book fs-16 text-info"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="fw-bold mb-1">Course</h6>
                    <p className="fw-medium mb-0">{courseName}</p>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="d-flex">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-sm bg-light-success rounded">
                      <i className="ti ti-coin fs-16 text-success"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="fw-bold mb-1">Fee Type</h6>
                    <p className="fw-medium mb-0 text-capitalize">
                      {feeShare.feeType?.type === "registrationfee"
                        ? "Registration Fee"
                        : feeShare.feeType?.type || "Registration Fee"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="d-flex">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-sm bg-light-warning rounded">
                      <i className="ti ti-percentage fs-16 text-warning"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="fw-bold mb-1">Share Type</h6>
                    <p className="fw-medium mb-0">
                      {feeShare.share_type === "fixed" ? (
                        <span className="badge badge-soft-primary d-inline-flex align-items-center">
                          <i className="ti ti-circle-filled fs-5 me-1"></i>
                          Fixed Amount (₹)
                        </span>
                      ) : (
                        <span className="badge badge-soft-success d-inline-flex align-items-center">
                          <i className="ti ti-circle-filled fs-5 me-1"></i>
                          Percentage (%)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="d-flex">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-sm bg-light-secondary rounded">
                      <i className="ti ti-clock fs-16 text-secondary"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="fw-bold mb-1">Created On</h6>
                    <p className="fw-medium mb-0">
                      {formatDateToYMD(feeShare.created_at)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="d-flex">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-sm bg-light-secondary rounded">
                      <i className="ti ti-refresh fs-16 text-secondary"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="fw-bold mb-1">Last Updated</h6>
                    <p className="fw-medium mb-0">
                      {formatDateToYMD(feeShare.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fee Details Card */}
        <div className="card mb-3">
          <div className="card-header bg-light">
            <div className="d-flex align-items-center">
              <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                <i className="ti ti-list-details fs-16" />
              </span>
              <h4 className="text-dark mb-0">Registration Fee Details</h4>
            </div>
          </div>
          <div className="card-body pb-1">
            {isLoadingRegistrationFeeConfig ? (
              <div className="text-center py-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading fee configuration...</p>
              </div>
            ) : registrationFeeAmount > 0 ? (
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>Registration Fee Amount</th>
                      <th>
                        Share Amount (
                        {feeShare.share_type === "fixed" ? "₹" : "%"})
                      </th>
                      {feeShare.share_type === "fixed" ? (
                        <th>Share Percentage</th>
                      ) : (
                        <th>Fixed Amount (₹)</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>₹{registrationFeeAmount.toLocaleString()}</td>
                      <td>
                        {feeShare.share_type === "fixed"
                          ? `₹${feeShare.share_amount.toLocaleString()}`
                          : `${feeShare.share_amount}%`}
                      </td>
                      {feeShare.share_type === "fixed" ? (
                        <td>{calculatePercentage().toFixed(2)}%</td>
                      ) : (
                        <td>
                          ₹
                          {(
                            (feeShare.share_amount / 100) *
                            registrationFeeAmount
                          ).toLocaleString()}
                        </td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="alert alert-warning mb-0">
                <i className="ti ti-alert-circle me-2"></i>
                Registration fee amount is not available for this course.
              </div>
            )}
          </div>
        </div>

        {/* Centers Card */}
        <div className="card mb-3">
          <div className="card-header bg-light">
            <div className="d-flex align-items-center">
              <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                <i className="ti ti-users fs-16" />
              </span>
              <h4 className="text-dark mb-0">Applied Centers</h4>
            </div>
          </div>
          <div className="card-body pb-1">
            {feeShare.all_centers ? (
              <div className="alert alert-info mb-0">
                <i className="ti ti-info-circle me-2"></i>
                This fee share is applied to all centers
              </div>
            ) : feeShare.centers && feeShare.centers.length > 0 ? (
              <div className="row">
                {feeShare.centers.map((center) => (
                  <div key={center.center_id} className="col-md-4 mb-3">
                    <div className="card border">
                      <div className="card-body p-3">
                        <h5 className="card-title mb-0">{center.name}</h5>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="alert alert-warning mb-0">
                <i className="ti ti-alert-circle me-2"></i>
                No centers found for this fee share
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewRegistrationFeeShare;
